import {
  getGetSuperAdminPackagesQueryKey,
  useDeleteSuperAdminPackagesPackageId,
  useGetSuperAdminModules,
  useGetSuperAdminPackages,
  usePatchSuperAdminPackagesPackageId,
  usePostSuperAdminPackages,
  usePostSuperAdminPackagesReorder,
  usePutSuperAdminPackagesPackageIdModules,
} from '@agos/api-client/platform'
import { T, formatCount, formatMoney, tt } from '@agos/shared'
import {
  PageHeader,
  cn,
  ConfirmDialog,
  IconButton,
  EmptyState,
  toast,
  ReadFailed,
  StatusBadge,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronDown, ChevronUp, Package, Pencil, Plus, Save, Trash2, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  categoryIsFull,
  draftProblem,
  groupByCategory,
  modulesOf,
  packageModuleSummary,
  packagesOf,
  reorderedIds,
  toggledCategory,
  toggledModule,
  type CatalogPackage,
} from './catalog'

/**
 * The tiers a franchise can be on, and what each one includes - §3.3, FB-506.
 *
 * **This page was read-only and five endpoints had no caller**: create, patch, delete, reorder and
 * replace-modules. §3's own note calls this pair *"drives tenant module-gating everywhere else"*, so
 * a read-only tier catalogue means a platform that cannot change what any franchise is allowed to do.
 *
 * **Saving an edit is two calls in order** - `PATCH` the package, then `PUT` its modules. The modules
 * call replaces the whole list, so doing it first would leave a tier whose modules had moved and
 * whose price had not if the second failed.
 *
 * **The slug is sent on create and never on edit**, which is legacy's own `PATCH` shape: it is what a
 * franchise's `package` join is written against.
 */

const THEMES = [
  { value: 'classic', label: 'Classic' },
  { value: 'modern-minimal', label: 'Modern Minimal' },
  { value: 'cozy-bistro', label: 'Cozy Bistro' },
] as const

interface Draft {
  slug: string
  name: string
  description: string
  monthlyPrice: string
  defaultTheme: string
  isActive: boolean
  modules: string[]
}

function draftOf(one: CatalogPackage): Draft {
  return {
    slug: one.slug,
    name: one.name,
    description: one.description ?? '',
    monthlyPrice: one.monthlyPrice,
    defaultTheme: one.defaultTheme,
    isActive: one.isActive,
    modules: [...one.modules],
  }
}

function emptyDraft(): Draft {
  return {
    slug: '',
    name: '',
    description: '',
    monthlyPrice: '0',
    defaultTheme: 'classic',
    isActive: true,
    modules: [],
  }
}

export function PackagesPage() {
  const queryClient = useQueryClient()
  const packagesQuery = useGetSuperAdminPackages()
  const modulesQuery = useGetSuperAdminModules()
  // §3.107. This page already gated its nothing-state on `isLoading` and not on failure, so a
  // refused read said *Žiadne balíky* - the tier catalogue somebody sells on, reporting itself
  // empty because the server did not answer.
  const readFailed = packagesQuery.isError || modulesQuery.isError

  const packages = useMemo(() => packagesOf(packagesQuery.data), [packagesQuery.data])
  const modules = useMemo(() => modulesOf(modulesQuery.data), [modulesQuery.data])
  const groups = useMemo(() => groupByCategory(modules), [modules])

  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [problem, setProblem] = useState('')
  const [pendingDeletion, setPendingDeletion] = useState<CatalogPackage | null>(null)

  const create = usePostSuperAdminPackages()
  const patch = usePatchSuperAdminPackagesPackageId()
  const replaceModules = usePutSuperAdminPackagesPackageIdModules()
  const remove = useDeleteSuperAdminPackagesPackageId()
  const reorder = usePostSuperAdminPackagesReorder()

  const isWorking =
    create.isPending || patch.isPending || replaceModules.isPending || remove.isPending || reorder.isPending

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetSuperAdminPackagesQueryKey() })

  const failed = (caught: unknown, fallback: string) => {
    setProblem(caught instanceof Error && caught.message !== '' ? caught.message : fallback)
  }

  const save = async () => {
    if (draft === null) return
    const stop = draftProblem(draft, editing === 'new')
    if (stop !== null) {
      setProblem(stop)
      return
    }
    setProblem('')
    try {
      if (editing === 'new') {
        await create.mutateAsync({
          data: {
            slug: draft.slug.trim(),
            name: draft.name.trim(),
            description: draft.description.trim() === '' ? undefined : draft.description.trim(),
            monthlyPrice: draft.monthlyPrice,
            defaultTheme: draft.defaultTheme,
            isActive: draft.isActive,
            modules: draft.modules,
          },
        })
      } else if (typeof editing === 'number') {
        // §3.3 - the package first, then its modules. No slug: it is immutable once a tier exists.
        await patch.mutateAsync({
          packageId: editing,
          data: {
            name: draft.name.trim(),
            description: draft.description.trim() === '' ? undefined : draft.description.trim(),
            monthlyPrice: draft.monthlyPrice,
            defaultTheme: draft.defaultTheme,
            isActive: draft.isActive,
          },
        })
        await replaceModules.mutateAsync({ packageId: editing, data: { modules: draft.modules } })
      }
      setEditing(null)
      setDraft(null)
      refresh()
      toast({ title: tt('Balíček uložený') })
    } catch (caught) {
      failed(caught, tt('Uloženie zlyhalo'))
    }
  }

  const move = async (one: CatalogPackage, direction: -1 | 1) => {
    const ids = reorderedIds(packages, one.id, direction)
    if (ids === null) return
    setProblem('')
    try {
      // The whole list: the server reassigns `sortOrder = (i + 1) * 10` across all of them.
      await reorder.mutateAsync({ data: { ids } })
      refresh()
    } catch (caught) {
      failed(caught, tt('Zmena poradia zlyhala'))
    }
  }

  const erase = async (one: CatalogPackage) => {
    setProblem('')
    try {
      await remove.mutateAsync({ packageId: one.id })
      refresh()
      toast({ title: tt('Balíček zmazaný'), description: one.name })
    } catch (caught) {
      failed(caught, tt('Vymazanie zlyhalo'))
    }
  }

  return (
    <main className="space-y-3" data-packages>
      <PageHeader
        title={tt('Balíky')}
        description={tt('Čo každý balík obsahuje a čo stojí.')}
        icon={<Package className="w-5 h-5" />}
      />
      {/* §3.107 - said above the list, so an operator reading a short list knows whether it is
        * short or simply unanswered. */}
      {readFailed && (
        <ReadFailed marker="packages-error"><T>Katalóg balíkov sa nepodarilo načítať.</T></ReadFailed>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">
          <T>Balíčky a ich moduly. Čo balíček obsahuje, rozhoduje o tom, čo franšíza smie používať.</T>
        </p>
        {editing === null && (
          <button
            type="button"
            data-new-package
            onClick={() => {
              setEditing('new')
              setDraft(emptyDraft())
              setProblem('')
            }}
            className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold"
          >
            <Plus className="w-4 h-4" /> <T>Nový balíček</T>
          </button>
        )}
      </div>

      {problem !== '' && (
        <p className="text-xs text-red-500" data-package-problem>
          {problem}
        </p>
      )}

      {editing === 'new' && draft !== null && (
        <PackageForm
          draft={draft}
          isNew
          groups={groups}
          moduleCount={modules.length}
          isWorking={isWorking}
          onChange={setDraft}
          onSave={() => void save()}
          onCancel={() => {
            setEditing(null)
            setDraft(null)
            setProblem('')
          }}
        />
      )}

      <div className="space-y-2">
        {packages.map((one, index) => {
          const isEditing = editing === one.id
          return (
            <section
              key={one.slug}
              data-package={one.slug}
              className={cn(
                'rounded-2xl border p-3 space-y-2',
                isEditing ? 'border-primary' : 'border-border',
              )}
            >
              <div className="flex flex-wrap items-baseline gap-2">
                <p className="text-sm font-semibold">{one.name}</p>
                <span className="font-mono text-[11px] text-muted-foreground">{one.slug}</span>
                {/* §3.134 - **`€{one.monthlyPrice}` printed `€149.00`**: the currency before the number and a
                    full stop for a decimal comma, on the one Tower figure of 44 that had no formatter. Legacy's
                    own `Packages.tsx` does the same and has a `formatCurrency` it does not use there, so this is
                    the port rather than a departure - `formatMoney`'s javadoc says it is byte-identical to that
                    helper. §4.4 made the same argument for the public site's copy of this field. */}
                <span className="text-sm font-bold" data-package-price>
                  {formatMoney(one.monthlyPrice)}
                </span>
                <span className="text-[11px] text-muted-foreground" data-package-modules>
                  {formatCount(one.modules.length)} / {formatCount(modules.length)} <T>modulov</T>
                </span>
                {/* §3.96 - bare 11 px text with **no colour at all**: an inactive package read
                  * exactly like an active one, on the screen that decides what every tenant is sold. */}
                <span data-package-active={one.isActive ? 'yes' : 'no'}>
                  <StatusBadge
                    label={one.isActive ? tt('Aktívny') : tt('Neaktívny')}
                    color={one.isActive ? 'emerald' : 'zinc'}
                  />
                </span>

                {!isEditing && (
                  <span className="ml-auto flex items-center gap-1">
                    {/* §3.118 - the product's own row-action button. These four carried
                      * `p-1.5 rounded-lg border border-border`, which is `iconButtonClass`'s base string
                      * character for character, so they were copies of a component that already
                      * existed - §9.74's finding, in the app it never swept. */}
                    <IconButton
                      label={tt('Posunúť vyššie')}
                      disabled={index === 0 || isWorking}
                      onClick={() => void move(one, -1)}
                      attributes={{ 'data-package-up': one.slug }}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </IconButton>
                    <IconButton
                      label={tt('Posunúť nižšie')}
                      disabled={index === packages.length - 1 || isWorking}
                      onClick={() => void move(one, 1)}
                      attributes={{ 'data-package-down': one.slug }}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </IconButton>
                    <IconButton
                      label={`${tt('Upraviť')} ${one.name}`}
                      onClick={() => {
                        setEditing(one.id)
                        setDraft(draftOf(one))
                        setProblem('')
                      }}
                      attributes={{ 'data-package-edit': one.slug }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </IconButton>
                    <IconButton
                      label={`${tt('Zmazať')} ${one.name}`}
                      tone="red"
                      onClick={() => setPendingDeletion(one)}
                      attributes={{ 'data-package-delete': one.slug }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </IconButton>
                  </span>
                )}
              </div>

              {one.description !== null && !isEditing && (
                <p className="text-[11px] text-muted-foreground">{one.description}</p>
              )}

              {/* §3.138 - the count above says *how many*, and until this existed the only way to see
                * *which* was to press *Upraviť*: a read behind the button whose job is to change the
                * tier that module-gates every franchise on it. Legacy has listed them all along. */}
              {!isEditing && <ModuleList chosen={one.modules} groups={groups} />}

              {isEditing && draft !== null && (
                <PackageForm
                  draft={draft}
                  isNew={false}
                  groups={groups}
                  moduleCount={modules.length}
                  isWorking={isWorking}
                  onChange={setDraft}
                  onSave={() => void save()}
                  onCancel={() => {
                    setEditing(null)
                    setDraft(null)
                    setProblem('')
                  }}
                />
              )}
            </section>
          )
        })}
      </div>

      {packages.length === 0 && !packagesQuery.isLoading && !readFailed && (
        <div data-no-packages>
          <EmptyState
            icon={<Package className="w-12 h-12" />}
            title={tt('Žiadne balíky')}
            description={tt('Balík určuje, ktoré moduly franšíza dostane a za čo.')}
          />
        </div>
      )}

      {/* §3.3 - legacy warns in the `confirm()` itself that a franchise on this tier has to be moved
        * off it first. A `ConfirmDialog`, as every destructive action on the manager uses. */}
      <ConfirmDialog
        open={pendingDeletion !== null}
        onClose={() => setPendingDeletion(null)}
        onConfirm={() => {
          const one = pendingDeletion
          setPendingDeletion(null)
          if (one !== null) void erase(one)
        }}
        variant="danger"
        title={tt('Zmazať balíček?')}
        description={`${pendingDeletion?.name ?? ''} - ${tt('franšízy na ňom treba najprv presunúť na iný balíček.')}`}
        confirmLabel={tt('Zmazať')}
      />
    </main>
  )
}

/**
 * What the tier includes, as legacy's card has always shown it - §3.138.
 *
 * Capped, because a tier holding every module would otherwise push the next card off the screen; the
 * cap and the naming are `packageModuleSummary`'s, so the rule is tested once rather than here.
 */
function ModuleList({
  chosen,
  groups,
}: {
  chosen: readonly string[]
  groups: ReturnType<typeof groupByCategory>
}) {
  const summary = packageModuleSummary(chosen, groups)

  if (summary.labels.length === 0) {
    return (
      <p className="text-[11px] italic text-muted-foreground" data-package-no-modules>
        <T>Žiadne moduly</T>
      </p>
    )
  }

  return (
    <ul className="flex flex-wrap gap-1" data-package-module-list>
      {summary.labels.map((label) => (
        <li
          key={label}
          className="rounded-lg bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground"
        >
          {label}
        </li>
      ))}
      {summary.extra > 0 && (
        <li className="px-1.5 py-0.5 text-[11px] italic text-muted-foreground" data-package-module-more>
          + {formatCount(summary.extra)} <T>ďalších</T>
        </li>
      )}
    </ul>
  )
}

function PackageForm({
  draft,
  isNew,
  groups,
  moduleCount,
  isWorking,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Draft
  isNew: boolean
  groups: ReturnType<typeof groupByCategory>
  moduleCount: number
  isWorking: boolean
  onChange: (next: Draft) => void
  onSave: () => void
  onCancel: () => void
}) {
  const field = 'min-h-[36px] rounded-xl border border-border bg-card px-2 text-sm'

  return (
    <div className="space-y-2 rounded-xl border border-border p-3" data-package-form={isNew ? 'new' : 'edit'}>
      <div className="flex flex-wrap gap-2">
        {isNew && (
          <input
            value={draft.slug}
            onChange={(event) => onChange({ ...draft, slug: event.target.value })}
            placeholder={tt('slug (a-z, 0-9, _, -)')}
            aria-label={tt('Slug')}
            data-package-slug-input
            className={cn(field, 'w-44 font-mono')}
          />
        )}
        <input
          value={draft.name}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
          placeholder={tt('Názov balíčka')}
          aria-label={tt('Názov balíčka')}
          data-package-name-input
          className={cn(field, 'flex-1 min-w-[160px]')}
        />
        <input
          value={draft.monthlyPrice}
          onChange={(event) => onChange({ ...draft, monthlyPrice: event.target.value })}
          aria-label={tt('Cena za mesiac')}
          data-package-price-input
          className={cn(field, 'w-24 text-right')}
        />
        <select
          value={draft.defaultTheme}
          onChange={(event) => onChange({ ...draft, defaultTheme: event.target.value })}
          aria-label={tt('Predvolená téma')}
          data-package-theme
          className={cn(field, 'w-44')}
        >
          {THEMES.map((theme) => (
            <option key={theme.value} value={theme.value}>
              {theme.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={draft.isActive}
            onChange={(event) => onChange({ ...draft, isActive: event.target.checked })}
            data-package-active-input
            className="w-4 h-4 rounded accent-primary"
          />
          <T>Aktívny</T>
        </label>
      </div>

      <textarea
        value={draft.description}
        onChange={(event) => onChange({ ...draft, description: event.target.value })}
        placeholder={tt('Popis balíčka')}
        aria-label={tt('Popis balíčka')}
        data-package-description-input
        rows={2}
        className="w-full rounded-xl border border-border bg-card px-2 py-1 text-xs"
      />

      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <T>Moduly</T> ({formatCount(draft.modules.length)} / {formatCount(moduleCount)})
      </p>

      <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-module-picker>
        {groups.map((group) => {
          const full = categoryIsFull(draft.modules, group.modules)
          return (
            <div key={group.category} data-picker-category={group.category}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-muted-foreground">{group.label}</span>
                <button
                  type="button"
                  data-picker-toggle={group.category}
                  onClick={() =>
                    onChange({
                      ...draft,
                      modules: toggledCategory(draft.modules, group.modules, !full),
                    })
                  }
                  className="text-[10px] text-primary"
                >
                  {full ? tt('Žiadne') : tt('Všetky')}
                </button>
              </div>
              <div className="space-y-0.5">
                {group.modules.map((module) => {
                  const chosen = draft.modules.includes(module.key)
                  return (
                    <button
                      key={module.key}
                      type="button"
                      data-picker-module={module.key}
                      data-picker-on={chosen ? 'yes' : 'no'}
                      title={module.alwaysOn ? tt('Vždy zapnuté') : (module.description ?? '')}
                      disabled={module.alwaysOn}
                      onClick={() => onChange({ ...draft, modules: toggledModule(draft.modules, module.key) })}
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-1 rounded-lg text-xs text-left',
                        chosen ? 'bg-primary/10 text-primary' : 'bg-muted/40 text-muted-foreground',
                        module.parentKey !== null && 'ml-4',
                        module.alwaysOn && 'opacity-60 cursor-not-allowed',
                      )}
                    >
                      <span
                        className={cn(
                          'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0',
                          chosen ? 'bg-primary border-primary' : 'border-border',
                        )}
                      >
                        {chosen && <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />}
                      </span>
                      <span className="flex-1 truncate">{module.name}</span>
                      {module.alwaysOn && (
                        <span className="text-[9px] text-amber-500" data-picker-always-on>
                          <T>vždy</T>
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-package-save
          disabled={isWorking}
          onClick={onSave}
          className="flex-1 inline-flex items-center justify-center gap-1.5 min-h-[36px] rounded-xl bg-primary text-primary-foreground text-sm font-bold disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> <T>Uložiť</T>
        </button>
        <button
          type="button"
          data-package-cancel
          onClick={onCancel}
          aria-label={tt('Zrušiť')}
          className="min-h-[36px] px-3 rounded-xl border border-border text-sm"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
